import { Badge, Button, Popover, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  TitleNoMargin,
  NotificationsPopoverContent,
  HeaderWrapper,
  FooterWrapper,
  SmileOutlinedIcon,
  EmptyNotifications,
  StyledBadge,
} from './NotificationsPopover.styled';
import { CheckCircleOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { StyledBellOutlined } from '../../AppHeader.styled';
import { useAppNavigation } from 'src/routes';
import { AlertNotification } from 'src/components/AlertNotification/AlertNotification';
import { useGlobal } from 'src/contexts/GlobalContext';
import { NotificationExtended } from 'src/api/generated';
import MobileFullPage from 'src/components/MobileFullPage';
import { notificationsStore } from 'src/stores/notificationsStore';
import useStore from 'src/stores/useStore';
import { NotificationsState, clearAllNotifications } from 'src/stores/notificationsStore';
import { LOCAL_STORAGE_KEYS } from 'src/utils/constants/localStorageConstants';
import useLocalStorage from 'use-local-storage';

const NotificationsPopover = () => {
  const { isDesktop } = useGlobal();
  const { t } = useTranslation();
  const { navigateTo } = useAppNavigation();
  const notifications = useStore<NotificationsState, NotificationExtended[]>(
    notificationsStore,
    (state) => state.notifications,
  );
  const [unreadNotifications, setUnreadNotifications] = useLocalStorage(LOCAL_STORAGE_KEYS.unread_notifications, false);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const onBellHandler = () => {
    isDesktop ? setPopoverOpen(!popoverOpen) : setIsOpen(true);

    setUnreadNotifications(false);
  };

  const onOpenChange = (open: boolean) => {
    if (isDesktop) {
      setPopoverOpen(open);
      setUnreadNotifications(false);
    }
  };

  const onSeeAllNotifications = () => {
    navigateTo.notifications();
    setPopoverOpen(false);

    setUnreadNotifications(false);
  };

  const onCloseMobileFullPage = () => {
    setIsOpen(false);
    setUnreadNotifications(false);
  };

  return (
    <>
      {isDesktop ? null : (
        <MobileFullPage
          isOpen={isOpen}
          headerTitle={t('notifications.title')}
          onClose={onCloseMobileFullPage}
          headerChildren={
            <Button
              onClick={clearAllNotifications}
              size="small"
              type="text"
              iconPosition="end"
              icon={<CheckCircleOutlined />}
            >
              {t('general.clearAll')}
            </Button>
          }
        >
          {notifications.length === 0 ? (
            <EmptyNotifications>
              <SmileOutlinedIcon />
              <Typography.Text type="secondary">{t('notifications.noNotifications')}</Typography.Text>
            </EmptyNotifications>
          ) : (
            <AlertNotification items={notifications} isAllNotifications={true} />
          )}
        </MobileFullPage>
      )}
      <Popover
        open={popoverOpen}
        styles={{
          body: {
            padding: 0,
          },
        }}
        onOpenChange={onOpenChange}
        arrow={false}
        trigger="click"
        content={
          <NotificationsPopoverContent vertical>
            <HeaderWrapper>
              <TitleNoMargin level={4}>
                {t('notifications.title')} <Badge count={notifications.length} />
              </TitleNoMargin>
              <Button
                onClick={clearAllNotifications}
                size="small"
                type="text"
                iconPosition="end"
                icon={<CheckCircleOutlined />}
              >
                {t('general.clearAll')}
              </Button>
            </HeaderWrapper>
            {notifications.length === 0 ? (
              <EmptyNotifications>
                <SmileOutlinedIcon />
                <Typography.Text type="secondary">{t('notifications.noNotifications')}</Typography.Text>
              </EmptyNotifications>
            ) : (
              <AlertNotification items={notifications} isAllNotifications={false} />
            )}
            {notifications.length === 0 ? null : (
              <FooterWrapper justify="flex-end">
                <Button
                  size="small"
                  type="text"
                  onClick={onSeeAllNotifications}
                  icon={<ArrowRightOutlined />}
                  iconPosition="end"
                >
                  {t('notifications.seeAllNotifications')}
                </Button>
              </FooterWrapper>
            )}
          </NotificationsPopoverContent>
        }
      >
        <StyledBadge dot={notifications.length > 0 && unreadNotifications}>
          <Button data-testid="bell-btn" type="text" onClick={onBellHandler} icon={<StyledBellOutlined />} />
        </StyledBadge>
      </Popover>
    </>
  );
};

export default NotificationsPopover;
